'use client';

import { useApiInvalidate } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { useSeonSession } from '@shared/hooks';
import { formatCurrencyAmountAsFloat, ROUTES, useClientConfig } from '@shared/utils';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import { useGlobalSheet } from '../../context/GlobalSheetContext';
import { PaymentsSheet } from '../PaymentsSheet/PaymentsSheet';
import { DepositFlowOrchestrator } from './DepositFlowOrchestrator';

// Browser API wrappers for testability
const timers = {
  setTimeout: (callback: () => void, delay: number): NodeJS.Timeout => setTimeout(callback, delay),
  clearTimeout: (timeoutId: NodeJS.Timeout | null): void => {
    if (timeoutId) clearTimeout(timeoutId);
  },
};

const KENOGO_PLAY_GAME_PATHS = new Set<string>([ROUTES.KENOGO.CLASSIC, ROUTES.KENOGO.JACKPOT, ROUTES.KENOGO.HEAD_OR_TAILS]);

const pathUtils = {
  isGamesPage: (pathname: string): boolean => /^\/casino\/[^/]+\/play/.test(pathname) || KENOGO_PLAY_GAME_PATHS.has(pathname),
};

function DepositSheet() {
  const {
    isDepositSheetOpen,
    closeDepositSheet,
    openDepositSheet,
    depositSheetTitle,
    resetDepositSheetTitle,
    isDepositSheetHeaderVisible,
    depositSheetOnSuccessCallback,
    minimumAmountForBonus,
    initialAmount,
    redirectUrlOnInsufficientDeposit,
    autoProcessPaymentMethod,
    skipSuccessStep,
    redirectUrlOnSuccess,
    openOnDepositLimits,
  } = useGlobalSheet();
  useSeonSession({ preload: isDepositSheetOpen });
  const { refetchQueriesStartingWith } = useApiInvalidate();
  const { session } = useSession();
  const pathname = usePathname();
  const { currency } = useClientConfig();
  const [returnPage, setReturnPage] = useState<string>();
  const [isFirstStep, setIsFirstStep] = useState(true);
  const [depositErrorStep, setDepositErrorStep] = useState<keyof typeof import('./types').DEPOSIT_ERROR_STEP | null>(null);
  const [isTransactionInProgress, setIsTransactionInProgress] = useState(false);
  const [isRedirectionInProgress, setIsRedirectionInProgress] = useState(false);
  const [currentStepId, setCurrentStepId] = useState<string | null>('amount');
  const [isApplePayModalOpen, setIsApplePayModalOpen] = useState(false);
  const backHandlerRef = useRef<(() => void) | null>(null);
  const hasProcessedDeeplink = useRef(false);
  const applePayModalObserverRef = useRef<MutationObserver | null>(null);

  const readReturnPage = useEffectEvent(() => {
    setReturnPage(new URLSearchParams(window.location.search).get('returnpage') || undefined);
  });

  useEffect(() => {
    readReturnPage();
  });

  useEffect(() => {
    if (hasProcessedDeeplink.current || session.status === 'loading') {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const depositAmountParam = searchParams.get('deposit-amount');

    if (depositAmountParam && session.profile) {
      hasProcessedDeeplink.current = true;

      if (/^\d+$/.test(depositAmountParam)) {
        if (depositAmountParam === '0') {
          openDepositSheet({});
        } else {
          const amountInMinorUnits = Number.parseInt(depositAmountParam, 10);
          if (!Number.isNaN(amountInMinorUnits) && amountInMinorUnits > 0) {
            const formattedAmount = formatCurrencyAmountAsFloat(amountInMinorUnits, currency);

            openDepositSheet({
              initialAmount: formattedAmount,
            });
          }
        }
      }
    }

    if (depositAmountParam && !session.profile && session.status === 'unauthenticated') {
      // After registration, the session cookie is set before SessionProvider refreshes.
      // Wait for SessionSyncAfterRegistration to authenticate before discarding the deeplink.
      if (searchParams.get('registration-success') === 'true') {
        return;
      }

      hasProcessedDeeplink.current = true;
      const url = new URL(window.location.href);
      url.searchParams.delete('deposit-amount');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, [currency, pathname, session, openDepositSheet]);

  const handleClose = useCallback(() => {
    if (isTransactionInProgress && !isRedirectionInProgress) return;
    if (isApplePayModalOpen) return;

    // Refetch all wallet queries to update available balance when closing
    refetchQueriesStartingWith('/api/client/v1/players/');
    refetchQueriesStartingWith('/api/client/v2/players/');

    resetDepositSheetTitle();
    closeDepositSheet();
  }, [closeDepositSheet, resetDepositSheetTitle, refetchQueriesStartingWith, isTransactionInProgress, isRedirectionInProgress, isApplePayModalOpen]);

  const handleStepChange = useCallback((currentIsFirstStep: boolean) => {
    setIsFirstStep(currentIsFirstStep);
  }, []);

  const handleStateChange = useCallback(
    (state: { isTransactionInProgress: boolean; isRedirectionInProgress: boolean; depositErrorStep: keyof typeof import('./types').DEPOSIT_ERROR_STEP | null }) => {
      setIsTransactionInProgress(state.isTransactionInProgress);
      setIsRedirectionInProgress(state.isRedirectionInProgress);
      setDepositErrorStep(state.depositErrorStep);
    },
    []
  );

  const handleBackHandlerReady = useCallback((handler: () => void) => {
    backHandlerRef.current = handler;
  }, []);

  const handleStepIdChange = useCallback((stepId: string | null) => {
    setCurrentStepId(stepId);
  }, []);

  const handleBackClick = useCallback(() => {
    if (isTransactionInProgress && !isRedirectionInProgress) {
      return;
    }
    if (isApplePayModalOpen) {
      return;
    }
    // Call the orchestrator's back handler if available, otherwise close the sheet
    if (backHandlerRef.current) {
      backHandlerRef.current();
    } else {
      handleClose();
    }
  }, [handleClose, isTransactionInProgress, isRedirectionInProgress, isApplePayModalOpen]);

  // Monitor for Apple Pay modal presence
  useEffect(() => {
    const checkApplePayModal = () => {
      const applePayModal = document.querySelector('apple-pay-modal');
      const isOpen = applePayModal?.shadowRoot?.querySelector('.modal')?.classList.contains('modal-open') ?? false;
      setIsApplePayModalOpen(isOpen);
    };

    // Check initially
    checkApplePayModal();

    // Set up observer to watch for Apple Pay modal changes
    applePayModalObserverRef.current = new MutationObserver(() => {
      checkApplePayModal();

      // If modal appears, also observe its shadowRoot
      const applePayModal = document.querySelector('apple-pay-modal');
      if (applePayModal?.shadowRoot && applePayModalObserverRef.current) {
        applePayModalObserverRef.current.observe(applePayModal.shadowRoot, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
        });
      }
    });

    // Observe document body for Apple Pay modal additions/removals
    applePayModalObserverRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Also observe the modal's shadowRoot if it already exists
    const applePayModal = document.querySelector('apple-pay-modal');
    if (applePayModal?.shadowRoot && applePayModalObserverRef.current) {
      applePayModalObserverRef.current.observe(applePayModal.shadowRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    return () => {
      applePayModalObserverRef.current?.disconnect();
    };
  }, []);

  return (
    <PaymentsSheet
      title={depositSheetTitle}
      data-track-feature="Deposit"
      showBackButton={!isFirstStep && !depositErrorStep && !isTransactionInProgress}
      showHeader={isDepositSheetHeaderVisible}
      showCloseButton={isDepositSheetHeaderVisible && (isRedirectionInProgress || !isTransactionInProgress) && currentStepId !== 'method_details'}
      open={isTransactionInProgress && !isRedirectionInProgress ? true : isDepositSheetOpen}
      onOpenChange={open => {
        if (!open) {
          // Check synchronously if Apple Pay modal is open to prevent closing
          const applePayModal = document.querySelector('apple-pay-modal');
          const isApplePayOpen = applePayModal?.shadowRoot?.querySelector('.modal')?.classList.contains('modal-open') ?? false;

          if (!isApplePayOpen) {
            handleClose();
          }
        }
      }}
      onBack={handleBackClick}
      data-testid="deposit-sheet"
    >
      <DepositFlowOrchestrator
        onClose={handleClose}
        onStepChange={handleStepChange}
        onStateChange={handleStateChange}
        onBackHandlerReady={handleBackHandlerReady}
        onSuccessCallback={depositSheetOnSuccessCallback || undefined}
        minimumAmountForBonus={minimumAmountForBonus}
        initialAmount={initialAmount}
        redirectUrlOnInsufficientDeposit={redirectUrlOnInsufficientDeposit}
        autoProcessPaymentMethod={autoProcessPaymentMethod}
        skipSuccessStep={skipSuccessStep}
        redirectUrlOnSuccess={redirectUrlOnSuccess ?? returnPage}
        openOnDepositLimits={openOnDepositLimits}
        onStepIdChange={handleStepIdChange}
      />
    </PaymentsSheet>
  );
}

export { DepositSheet, pathUtils, timers };
